/**
 * DeepLinkMatcher
 * ────────────────────────────────────────────────────────────────────
 * Mounted ONCE at the App root (outside the lazy-loaded <Suspense> route
 * tree), so it is always present in the main bundle regardless of which
 * page chunk is still loading. This makes it the reliable place to
 * auto-open a matcher from a URL, which is critical for Facebook /
 * Instagram in-app browsers where the heavy destination page chunk can
 * load slowly or fail intermittently.
 *
 * Handles two URL-driven entry points:
 *
 *  1. `?openMatcher=<audience>`  (builder | supplier | trade | professional)
 *     Set by AudienceMatcherRoute for the shareable ad links
 *     `/find/builder` etc. Opens the full audience mini-matcher
 *     (categoryOptions + heading) immediately — it does NOT wait for the
 *     destination hub page's <Header> to mount, which previously made the
 *     matcher open only "sometimes" in in-app browsers.
 *
 *  2. `?open_matcher=1&category=...`  (business "Connect with X" button)
 *     Opens a MiniMatcher pre-loaded for that business + category.
 *
 * Reads URL params on every history change, opens the matcher when a flag
 * is present, and strips the params off the URL when the matcher closes so
 * a back-button / refresh navigation doesn't pop the matcher up again.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MiniMatcher from './MiniMatcher';
import {
  BUILDERS_MATCHER,
  SUPPLIERS_MATCHER,
  TRADES_MATCHER,
  PROS_MATCHER,
} from '../../data/megaMenuMatchers';

const AUDIENCE_MATCHERS = {
  builder: BUILDERS_MATCHER,
  supplier: SUPPLIERS_MATCHER,
  trade: TRADES_MATCHER,
  professional: PROS_MATCHER,
};

export default function DeepLinkMatcher() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState(null);     // business "Connect with X" deep-link
  const [audience, setAudience] = useState(null);  // /find/<audience> ad-link flow

  useEffect(() => {
    const qs = new URLSearchParams(location.search);

    // 1) Shareable audience ad links: /find/builder -> ?openMatcher=builder
    const aud = qs.get('openMatcher');
    if (aud && AUDIENCE_MATCHERS[aud]) {
      setAudience(aud);
      setParams(null);
      setOpen(true);
      return;
    }

    // 2) Business profile "Connect with X" deep-link
    if (qs.get('open_matcher') === '1') {
      setAudience(null);
      setParams({
        category: qs.get('category') || null,
        businessId: qs.get('business_id') || null,
        businessName: qs.get('business_name') || null,
      });
      setOpen(true);
    }
  }, [location.search]);

  const handleClose = () => {
    setOpen(false);
    // Strip every deep-link param so a back-button or refresh doesn't
    // pop the matcher again. Keep any other params the user had.
    const qs = new URLSearchParams(location.search);
    qs.delete('openMatcher');
    qs.delete('open_matcher');
    qs.delete('category');
    qs.delete('business_id');
    qs.delete('business_name');
    const next = qs.toString();
    navigate(
      { pathname: location.pathname, search: next ? `?${next}` : '' },
      { replace: true },
    );
  };

  if (!open) return null;

  // Audience ad-link flow (builder / supplier / trade / professional).
  if (audience && AUDIENCE_MATCHERS[audience]) {
    const cfg = AUDIENCE_MATCHERS[audience];
    return (
      <MiniMatcher
        open={open}
        onClose={handleClose}
        categoryOptions={cfg.categoryOptions}
        heading={cfg.heading}
        subheading={cfg.subheading}
        sourcePlacement={`find-${audience}-url`}
      />
    );
  }

  // Business profile deep-link flow.
  if (!params?.category) return null;

  // sourcePlacement uses 'profile-deeplink' so HQ analytics can split
  // out leads that arrived through a profile-page click vs ones that
  // came in via the mega-menu / fab / inline triggers.
  return (
    <MiniMatcher
      open={open}
      onClose={handleClose}
      category={params.category}
      sourcePlacement="profile-deeplink"
      heading={params.businessName ? `Connect with ${params.businessName}` : 'Find your match'}
      anchorSide="right"
    />
  );
}
